if (!customElements.get('product-form')) {
  const SPARK_WAIT_TIMEOUT_MS = 6000;
  const SPARK_WAIT_POLL_MS = 80;

  let sparkReadyPromise = null;
  let sparkCartInitPromise = null;

  function getSpark() {
    return window.sparklayer;
  }

  async function waitForSparkReadyCached(timeoutMs = SPARK_WAIT_TIMEOUT_MS) {
    if (sparkReadyPromise) return sparkReadyPromise;

    sparkReadyPromise = (async () => {
      const start = Date.now();
      while (Date.now() - start < timeoutMs) {
        const sl = getSpark();
        if (sl && typeof sl.updateCart === 'function') return sl;
        await new Promise((r) => setTimeout(r, SPARK_WAIT_POLL_MS));
      }
      return null;
    })();

    const sl = await sparkReadyPromise;

    if (!sl) sparkReadyPromise = null;

    return sl;
  }

  function ensureSparkCartOnce(sl) {
    if (sparkCartInitPromise) return sparkCartInitPromise;

    sparkCartInitPromise = (async () => {
      try {
        if (typeof sl.takeCartControl === 'function') {
          await sl.takeCartControl();
        }
      } catch (e) {
        console.warn('[SparkLayer] takeCartControl failed (non-fatal):', e);
      }
    })();

    return sparkCartInitPromise;
  }

  function openSparkDrawer(sl) {
    try {
      if (typeof sl.openDrawer === 'function') return sl.openDrawer();
    } catch (e) {
      console.warn('[SparkLayer] openDrawer failed (non-fatal):', e);
    }
  }

  async function sparkAddLine(sl, sku, qty) {
    const s = String(sku || '').trim();
    if (!s) throw new Error('Missing SKU for SparkLayer updateCart');

    const input = { products: [{ sku: s, adjustQuantity: qty }] };
    const res = await sl.updateCart(input, true, true);

    const errors =
      res?.errors ||
      res?.userErrors ||
      res?.data?.updateCart?.userErrors ||
      res?.data?.updateCart?.errors;

    if (errors && (Array.isArray(errors) ? errors.length : true)) {
      throw new Error('SparkLayer updateCart returned errors');
    }
  }

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.variantData = [];
        this.form = this.querySelector('form');

        this.variantIdInput && (this.variantIdInput.disabled = false);
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtons = document.querySelectorAll('product-info .product-form__submit[type="submit"]');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.isB2B = this.dataset.b2bCustomer === 'true' || this.dataset.b2bCustomer === '1';

        try {
          const el = document.getElementById(`ProductVariants-${this.dataset.sectionId}`);
          if (el?.textContent) this.variantData = JSON.parse(el.textContent);
        } catch (e) {
          console.warn('Variant JSON parse error', e);
        }

        if (this.isB2B) {
          waitForSparkReadyCached().then((sl) => {
            if (sl) ensureSparkCartOnce(sl);
          });
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButtons?.forEach((b) => b.setAttribute('aria-disabled', 'true'));
        this.submitButton?.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          if (this.isB2B) {
            await this.onSubmitB2B();
          } else {
            await this.onSubmitB2C(evt);
          }
        } catch (e) {
          console.error(e);
          this.handleErrorMessage(e?.message || 'Noe gikk galt. Prøv igjen.');
        } finally {
          this.submitButton?.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButtons?.forEach((b) => b.removeAttribute('aria-disabled'));
          this.toggleSubmitButton(false);
          this.querySelector('.loading__spinner')?.classList.add('hidden');

          if (window.CartPerformance?.measureFromEvent) {
            CartPerformance.measureFromEvent('add:user-action', evt);
          }
        }
      }

      async onSubmitB2B() {
        const variantId = String(this.form.querySelector('[name="id"]')?.value || '').trim();
        if (!variantId) {
          this.handleErrorMessage('Velg en variant og prøv igjen.');
          return;
        }

        const v = (this.variantData || []).find((x) => String(x.id) === variantId);
        const sku = v?.sku;

        if (!sku) {
          this.handleErrorMessage('Fant ikke SKU for valgt variant. Prøv å oppdatere siden.');
          return;
        }

        const qtyEl = (this.closest('product-info') || document).querySelector('.quantity__input[name="quantity"]');
        const qty = Math.max(1, parseInt(qtyEl?.value || '1', 10));

        const sl = await waitForSparkReadyCached();
        if (!sl) {
          this.handleErrorMessage('B2B-kurven lastes fortsatt. Prøv igjen om et øyeblikk.');
          return;
        }

        await ensureSparkCartOnce(sl);
        await sparkAddLine(sl, sku, qty);
        openSparkDrawer(sl);
      }

      async onSubmitB2C(evt) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((s) => s.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const repeaterEl = this.querySelector('#print-repeater') || this.closest('product-form')?.__printRepeater;
        const printToggleEl = this.querySelector('#print-toggle');
        let builtItems = null;

        if (printToggleEl?.checked && repeaterEl && typeof repeaterEl.buildCartItems === 'function') {
          builtItems = repeaterEl.buildCartItems();
          if (!builtItems || !builtItems.length) builtItems = null;
        }

        if (builtItems) {
          formData.delete('id');
          formData.delete('quantity');

          builtItems.forEach((it, i) => {
            formData.append(`items[${i}][id]`, String(it.id));
            formData.append(`items[${i}][quantity]`, String(it.quantity));
            if (it.properties) {
              Object.entries(it.properties).forEach(([k, v]) => {
                if (v != null && v !== '') formData.append(`items[${i}][properties][${k}]`, String(v));
              });
            }
          });
        } else {
          const id = this.form.querySelector('[name="id"]')?.value;
          const qtyEl = (this.closest('product-info') || document).querySelector('.quantity__input[name="quantity"]');
          const qty = Math.max(1, parseInt(qtyEl?.value || '1', 10));
          formData.append('id', id);
          formData.append('quantity', String(qty));
        }

        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config).then((r) => r.json());

        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);
          return;
        }

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(response));
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(response));
        }

        const openModal = document.querySelector('#PopupModal-product-form');
        if (openModal) openModal.hide();
        document.body.querySelector('product-info')?.dispatchEvent(new CustomEvent('modalClosed'));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButtons.forEach((button) => {
          const buttonText = button.querySelector('span');
          if (disable) {
            button.setAttribute('disabled', 'disabled');
            if (text && buttonText) buttonText.textContent = text;
          } else {
            button.removeAttribute('disabled');
            if (buttonText) buttonText.textContent = window.variantStrings.addToCart;
          }
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
